// ./src/components/generic/CategorySelect/CategorySelect.tsx
//
// Single-select over the reusable Category vocabulary. Reads options from the
// app-wide CategoriesContext (mounted in App.tsx, loaded once — categories are
// an account-global vocabulary, like tags). A category is reusable everywhere, so
// when `group` is set the picker offers categories in THAT group plus the
// always-shared "general" ones — keeping pickers tidy while preserving reuse.
// Modal-friendly (DRAWER_COMBOBOX_PROPS) so it works inside a drawer/sheet.
//
// The pinned "Create a category" action opens the real CategoryEditDrawer
// (pre-filled with this picker's group) and selects the new category on save —
// it lands in the shared context, so it's immediately offered everywhere else.

import React, { useMemo } from "react";
import { Group, Text } from "@mantine/core";

import { SelectField } from "@/components/generic/form";
import { useTranslation } from "react-i18next";

import { useOptionalCategoriesContext } from "@/contexts/CategoriesContext";
import { useLazyDrawer } from "@/hooks/useDrawerMount";
import { useToast } from "@/hooks/useToast";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";
import { Category, categoryIconColor, categoryIconName } from "@/utils/api";
import { AppIcon } from "@/components/generic/AppIcon";

import type { CategoryEditDrawerProps } from "@/components/features/Categories/CategoryEditDrawer/CategoryEditDrawer";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_IN_OWN_ACCOUNT } from "@/utils/createInPlace";

// The editor is a FEATURE component while this picker is generic, so it's pulled
// in lazily: keeps the generic layer free of a static import back into features
// (which would be a cycle) and out of the initial bundle.
// ⚠ NOT React.lazy (2026-10-03): a lazy drawer mounts ALREADY open, so it popped in with no slide and vanished on close.
//   `useLazyDrawer` loads it on the click, mounts it closed and opens it on the next commit. Type-only import above.
const loadCategoryDrawer = () =>
  import("@/components/features/Categories/CategoryEditDrawer/CategoryEditDrawer").then((m) => m.CategoryEditDrawer);

export interface CategorySelectProps {
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  /** The consumer group (e.g. "poi"). Offers this group + "general". Omit for all. */
  group?: string;
  /**
   * Show ONLY the `group`'s categories, without the shared "general" ones — for
   * pickers where general vocabulary (attire, gifts…) would be noise (e.g. lodging
   * types on an accommodation). No effect unless `group` is set.
   */
  exactGroup?: boolean;
  label?: string;
  description?: string;
  placeholder?: string;
  error?: React.ReactNode;
  disabled?: boolean;
  size?: string;
  clearable?: boolean;
  withinPortal?: boolean;
  /** Offer the pinned "Create a category" action (default true). */
  allowCreate?: boolean;
}

export const CategorySelect: React.FC<CategorySelectProps> = ({
  value,
  onChange,
  group,
  exactGroup = false,
  label,
  description,
  placeholder,
  error,
  disabled = false,
  size = "md",
  clearable = true,
  withinPortal,
  allowCreate = true,
}) => {
  const { t } = useTranslation(["common"]);
  // #142 — a category lands in the caller's own account: never offered to a crew member.
  const mayCreate = useCreateInPlace(CREATE_IN_OWN_ACCOUNT) && allowCreate;
  const toast = useToast();
  const createDrawer = useLazyDrawer<React.ComponentType<CategoryEditDrawerProps>>(loadCategoryDrawer, {
    onLoadError: () => toast.error(t("common:errors.drawerLoadFailed", { defaultValue: "Could not open that form — check your connection and try again." })),
  });
  const CategoryEditDrawer = createDrawer.Component;

  const ctx = useOptionalCategoriesContext();
  // ⓘ Memoised: a fresh `[]` on every render would change what the memos below depend on (#129).
  const categories = useMemo(() => ctx?.categories ?? [], [ctx?.categories]);
  const loading = ctx?.loading ?? false;

  // Map id → the resolved { icon, color } (own, else group fallback) so options
  // and the trigger look exactly like the category's row in the Library list.
  const meta = useMemo(() => {
    const map = new Map<string, { icon: string; color: string }>();

    for (const category of categories) {
      if (category.id) {
        map.set(category.id, { icon: categoryIconName(category), color: categoryIconColor(category) });
      }
    }

    return map;
  }, [categories]);

  const data = useMemo(() => {
    const options = categories
      .filter((category) => {
        if (!category.id) {
          return false;
        }

        if (!group) {
          return true;
        }

        const g = category.categoryGroup || "general";

        // exactGroup drops the shared "general" fallback — show only this group.
        return exactGroup ? g === group : g === group || g === "general";
      })
      // icon + color travel WITH the option so both the dropdown and the mobile
      // picker can show the category exactly like the Library list does.
      .map((category) => ({
        value: category.id as string,
        label: category.name || category.code || (category.id as string),
        icon: categoryIconName(category),
        color: categoryIconColor(category),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return options;
  }, [categories, group, exactGroup]);

  const handleCreated = (category: Category) => {
    createDrawer.close();

    if (category?.id) {
      onChange(category.id);
    }
  };

  const selectedMeta = value ? meta.get(value) : undefined;

  const comboboxProps = withinPortal === false ? { ...DRAWER_COMBOBOX_PROPS, withinPortal: false } : DRAWER_COMBOBOX_PROPS;

  return (
    <>
      <SelectField
        label={label}
        description={description}
        placeholder={placeholder ?? t("common:categorySelect.placeholder", { defaultValue: "Select a category" })}
        data={data}
        value={value ?? null}
        onChange={(_n, v) => onChange(v)}
        createLabel={mayCreate ? t("common:categorySelect.create", { defaultValue: "Create a category" }) : undefined}
        onCreate={mayCreate ? createDrawer.open : undefined}
        leftSection={
          selectedMeta ? <AppIcon iconName={selectedMeta.icon} size={16} color={selectedMeta.color} /> : undefined
        }
        error={error}
        disabled={disabled || loading}
        searchable
        clearable={clearable}
        nothingFoundMessage={t("common:categorySelect.empty", { defaultValue: "No categories found" })}
        comboboxProps={comboboxProps}
        size={size}
        renderOption={({ option }) => {
          const info = meta.get(option.value);

          return (
            <Group gap="xs" wrap="nowrap">
              {info ? <AppIcon iconName={info.icon} size={16} color={info.color} /> : null}
              <Text size={size}>{option.label}</Text>
            </Group>
          );
        }}
      />

      {/* Loaded on the first "Create", then kept mounted so it slides in and out. */}
      {CategoryEditDrawer ? (
        <CategoryEditDrawer opened={createDrawer.opened} onClose={createDrawer.close} initialGroup={group} onCreated={handleCreated} />
      ) : null}
    </>
  );
};
